import { useEffect, useState, useCallback, useRef } from 'react';
import { usePushTokenRegistration } from '@sendbird/uikit-react-native';
import {
  initializeNotifications,
  requestPermissions,
  getFCMToken
} from 'app/services/NotificationService';
import { useAuth } from 'app/auth/hooks/useAuth';
import { Telemetry } from 'app/services/telemetry/Telemetry';

export interface IPushNotificationState {
  isInitialized: boolean;
  fcmToken: string | null;
  permissionGranted: boolean;
  error: string | null;
}

export const usePushNotifications = () => {
  const [state, setState] = useState<IPushNotificationState>({
    isInitialized: false,
    fcmToken: null,
    permissionGranted: false,
    error: null,
  });

  const { registerPushTokenForCurrentUser } = usePushTokenRegistration();
  const { user } = useAuth();
  const initializingRef = useRef(false);

  const registerSendbirdPushToken = useCallback(async (): Promise<void> => {
    try {
      await registerPushTokenForCurrentUser();
    } catch (error) {
      Telemetry.error(error as Error, {
        event: 'push.register.token_sendbird_failed',
        domain: 'notifications',
        context: { source: 'usePushNotifications' }
      });
    }
  }, [registerPushTokenForCurrentUser]);

  const initializePushNotifications = useCallback(async (): Promise<void> => {
    if (initializingRef.current) {
      return;
    }

    initializingRef.current = true;

    try {
      setState(prev => ({ ...prev, error: null }));

      await initializeNotifications();

      const permissionGranted = await requestPermissions();

      if (!permissionGranted) {
        throw new Error('Notification permissions not granted');
      }

      const fcmToken = await getFCMToken();

      if (!fcmToken) {
        throw new Error('Failed to get FCM token');
      }

      if (user && fcmToken) {
        await registerSendbirdPushToken();
      }

      setState(prev => ({
        ...prev,
        isInitialized: true,
        fcmToken,
        permissionGranted,
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, error: errorMessage }));
      Telemetry.error(error as Error, {
        event: 'push.init.failed',
        domain: 'notifications',
        context: { source: 'usePushNotifications' }
      });
    } finally {
      initializingRef.current = false;
    }
  }, [user, registerSendbirdPushToken]);

  const refreshPushToken = useCallback(async (): Promise<void> => {
    try {
      const fcmToken = await getFCMToken();
      if (fcmToken && user) {
        await registerSendbirdPushToken();
        setState(prev => ({ ...prev, fcmToken }));
      }
    } catch (error) {
      Telemetry.error(error as Error, {
        event: 'push.refresh.failed',
        domain: 'notifications',
        context: { source: 'usePushNotifications' }
      });
    }
  }, [user, registerSendbirdPushToken]);

  useEffect(() => {
    if (user && !state.isInitialized && !initializingRef.current) {
      initializePushNotifications();
    }
  }, [user, state.isInitialized, initializePushNotifications]);

  return {
    ...state,
    initializePushNotifications,
    refreshPushToken,
  };
};
